// src/views/hospitality/BeBookingList.jsx
// Enterprise booking list — full feature set
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Table, Button, Input, message, Dropdown, Menu, Drawer, Modal, Tooltip, Select as AntSelect } from "antd";
import moment from "moment";
import CryptoJS from "crypto-js";
import {
  Phone, ChevronDown, Filter, Maximize2, Minimize2, RefreshCw,
  XCircle, CheckCircle, AlertTriangle, Eye, DollarSign,
  Edit3, AlertCircle, Copy, Mail, Globe, Calendar, User,
  Download, Tag, X, Home,
} from "react-feather";
import Select from "react-select";
import Swal from "sweetalert2";
import DatePicker from "react-bootstrap-daterangepicker";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";

import exportService from "../../pages/hms/utils/exportService";
import UserService from "../../pages/hms/utils/userService";
import { renderDate } from "../../pages/hms/utils/dateFormatter";
import PrintReportPreview from "../../pages/hms/reports/PrintReportPreview";
import PartnerService from "../../pages/hms/utils/partnerService";

// ─── Helpers ───────────────────────────────────────────────────────────────
const SYM = { INR:"₹",USD:"$",EUR:"€",GBP:"£",THB:"฿",AED:"د.إ" };
const fmtMoney = (v,cur="INR") => {
  const n=Number(v||0), s=SYM[cur]||cur||"";
  return `${s}${n.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
};
const fmtDate     = d => d ? moment(d).format("DD MMM YYYY") : "—";
const fmtDateTime = d => d ? moment(d).format("DD MMM YYYY, hh:mm A") : "—";
const copyText    = t => { navigator.clipboard?.writeText(t); message.success("Copied!"); };
const titleCaseLabel = value => {
  if (typeof value !== "string") return value;
  return value
    .replace(/\b\w/g, ch => ch.toUpperCase())
    .replace(/\bPms\b/g, "PMS")
    .replace(/\bId\b/g, "ID")
    .replace(/\bApi\b/g, "API")
    .replace(/\bOta\b/g, "OTA");
};

// ─── Metadata ──────────────────────────────────────────────────────────────
const STATUS_META = {
  reserved:    {label:"Reserved",   color:"#92400e",bg:"#fffbeb",dot:"#f59e0b",border:"#fde68a"},
  confirmed:   {label:"Confirmed",  color:"#065f46",bg:"#ecfdf5",dot:"#10b981",border:"#a7f3d0"},
  pending:     {label:"Pending",    color:"#1e3a8a",bg:"#eff6ff",dot:"#3b82f6",border:"#bfdbfe"},
  checked_in:  {label:"Checked In", color:"#134e4a",bg:"#f0fdfa",dot:"#14b8a6",border:"#99f6e4"},
  checked_out: {label:"Completed",  color:"#374151",bg:"#f9fafb",dot:"#9ca3af",border:"#e5e7eb"},
  cancelled:   {label:"Cancelled",  color:"#7f1d1d",bg:"#fef2f2",dot:"#ef4444",border:"#fecaca"},
  no_show:     {label:"No Show",    color:"#4c1d95",bg:"#f5f3ff",dot:"#8b5cf6",border:"#ddd6fe"},
};
const BOOKING_STATUS_OPTIONS = Object.entries(STATUS_META).map(([v,m])=>({value:v,label:m.label}));
const getS = s => STATUS_META[(s||"").toLowerCase()] || {label:(s||"").replace(/_/g," ").toUpperCase(),color:"#374151",bg:"#f9fafb",dot:"#9ca3af",border:"#e5e7eb"};

const REFUND_META = {
  none:          {label:"No Refund",        color:"#6b7280",bg:"#f9fafb",border:"#e5e7eb"},
  not_applicable:{label:"Manual Required",  color:"#92400e",bg:"#fffbeb",border:"#fde68a"},
  pending:       {label:"Refund Pending",   color:"#1e3a8a",bg:"#eff6ff",border:"#bfdbfe"},
  processed:     {label:"Refunded",         color:"#065f46",bg:"#ecfdf5",border:"#a7f3d0"},
  failed:        {label:"Refund Failed",    color:"#7f1d1d",bg:"#fef2f2",border:"#fecaca"},
  manual:        {label:"Manually Refunded",color:"#065f46",bg:"#ecfdf5",border:"#a7f3d0"},
};
const getR = r => REFUND_META[(r||"none").toLowerCase()] || REFUND_META.none;

const PAY_METHOD_MAP = {
  razorpay:"Razorpay",cash:"Cash",bank_transfer:"Bank Transfer",
  upi:"UPI",card:"Card",netbanking:"Net Banking",wallet:"Wallet",
};
const payLabel = m => PAY_METHOD_MAP[(m||"").toLowerCase()] || m || "—";

const roomTypeLines = booking => {
  const rows = Array.isArray(booking?.room_types) ? booking.room_types : [];
  return rows
    .filter(Boolean)
    .map((r,i)=>({
      key: r.room_type_id || `${r.room_type_name||"room"}-${i}`,
      name: r.room_type_name || "Room",
      quantity: Math.max(1, Number(r.quantity||1)),
      ratePlan: r.rate_plan_name || r.rate_plan_code || "",
    }));
};

const roomTypeLabel = booking => {
  const rows = roomTypeLines(booking);
  if(rows.length) return rows.map(r=>`${r.name}${r.quantity>1?` × ${r.quantity}`:""}`).join(", ");
  return booking?.room_type_names || booking?.primary_room_type_name || "Room type unavailable";
};

const primaryRatePlanLabel = booking => {
  const plans = roomTypeLines(booking).map(r=>r.ratePlan).filter(Boolean);
  return [...new Set(plans)].join(", ");
};

// ─── UI primitives ─────────────────────────────────────────────────────────
const Pill = ({label,color,bg,border,dot,sm}) => (
  <span style={{display:"inline-flex",alignItems:"center",gap:5,background:bg,color,
    border:`1px solid ${border||color+"33"}`,borderRadius:20,
    padding:sm?"2px 9px":"4px 12px",fontSize:sm?11:12,fontWeight:700,whiteSpace:"nowrap",lineHeight:1.4}}>
    <span style={{width:sm?6:7,height:sm?6:7,borderRadius:"50%",background:dot||color,flexShrink:0}}/>
    {label}
  </span>
);

// Drawer row — label + value
const DR = ({label,value,chip,last,mono,vs,copy}) => (
  <div style={{display:"flex",justifyContent:"space-between",alignItems:chip?"center":"flex-start",
    padding:"9px 0",borderBottom:last?"none":"1px solid #eef2f7",gap:16,minHeight:40}}>
    <span style={{fontSize:12.5,color:"#1e293b",fontWeight:700,flexShrink:0,minWidth:130,
      letterSpacing:"0.005em",paddingTop:chip?0:1}}>
      {titleCaseLabel(label)}
    </span>
    {chip ? <span>{value}</span> : (
      <span style={{fontSize:13,color:"#0f172a",fontWeight:650,textAlign:"right",
        fontFamily:mono?"'Courier New',monospace":undefined,wordBreak:"break-all",...vs}}>
        {value||"—"}
        {copy && value && <button onClick={()=>copyText(value)}
          style={{background:"none",border:"none",cursor:"pointer",color:"#64748b",marginLeft:4,padding:"0 2px"}}>
          <Copy size={10}/>
        </button>}
      </span>
    )}
  </div>
);

const DSection = ({icon,title}) => (
  <div style={{display:"flex",alignItems:"center",gap:7,padding:"14px 0 7px",
    borderBottom:"1px solid #dfe7f0",marginBottom:4}}>
    <span style={{color:"#475569",fontSize:15,display:"inline-flex",alignItems:"center"}}>{icon}</span>
    <span style={{fontSize:12.5,fontWeight:850,color:"#0f172a",letterSpacing:"0.01em"}}>
      {titleCaseLabel(title)}
    </span>
  </div>
);

// Timeline item
const TLItem = ({time,label,sub,done,warn}) => (
  <div style={{display:"flex",gap:12,marginBottom:16,alignItems:"flex-start"}}>
    <div style={{flexShrink:0,marginTop:2}}>
      <div style={{width:20,height:20,borderRadius:"50%",
        background:done?"#ecfdf5":warn?"#fffbeb":"#f1f5f9",
        border:`2px solid ${done?"#10b981":warn?"#f59e0b":"#e2e8f0"}`,
        display:"flex",alignItems:"center",justifyContent:"center"}}>
        {done && <CheckCircle size={10} color="#10b981"/>}
        {warn && <AlertCircle size={10} color="#f59e0b"/>}
      </div>
    </div>
    <div>
      <div style={{fontSize:13,fontWeight:700,color:"#0f172a"}}>{label}</div>
      {sub && <div style={{fontSize:11,color:"#94a3b8",marginTop:1}}>{sub}</div>}
      {time && <div style={{fontSize:11,color:"#94a3b8",marginTop:1}}>{time}</div>}
    </div>
  </div>
);

// ─── BOOKING DETAIL DRAWER ─────────────────────────────────────────────────
const BookingDrawer = ({open,booking:b,onClose,apiURL,companyId,onUpdated}) => {
  const [refundLoading,  setRL]  = useState(false);
  const [refundModal,    setRM]  = useState(false);   // manual refund modal
  const [rzpRefundModal, setRZM] = useState(false);   // razorpay refund modal (with editable amount)
  const [refundAmt,      setRA]  = useState("");
  const [refundNote,     setRN]  = useState("");
  const [markingRefund,  setMR]  = useState(false);
  const [statusModal,    setSM]  = useState(false);
  const [newStatus,      setNS]  = useState("");
  const [statusNote,     setSN]  = useState("");
  const [changingStatus, setCS]  = useState(false);
  const [syncingPms,     setSP]  = useState(false);
  const [syncSnapshot,   setSS]  = useState(null);

  useEffect(() => {
    if(!open) { setRM(false); setRZM(false); setSM(false); setRA(""); setRN(""); setNS(""); setSN(""); setSS(null); }
    if(open && b) { setNS(b.booking_status||""); setSS(null); }
  }, [open, b]);

  if(!b) return null;

  const cur         = b.currency_code||"INR";
  const sm          = getS(b.booking_status);
  const refSt       = (b.refund_status||"none").toLowerCase();
  const refM        = getR(refSt);
  const isRazorpay  = (b.payment_method||"").toLowerCase()==="razorpay";
  const isPaid      = Number(b.amount_paid||0) > 0;
  const isCancelled = (b.booking_status||"").toLowerCase()==="cancelled";
  const refundAmt_  = Number(b.refund_amount||0);
  const amtPaid     = Number(b.amount_paid||0);
  const amtDue      = Number(b.amount_due||0);
  const pmsIsSynced = syncSnapshot?.is_synch ?? !!b.is_synch;
  const pmsSynchAt  = syncSnapshot?.synch_at || b.synch_at || null;
  const pmsBookingNumber = syncSnapshot?.pms_booking_number || b.pms_booking_number || null;
  const pmsBookingStatus = String(b.pms_booking_status || "").toLowerCase();
  const bookedRoomLabel = roomTypeLabel(b);
  const bookedRatePlan  = primaryRatePlanLabel(b);
  const bookedRoomQty   = Number(b.rooms_quantity || 0) || roomTypeLines(b).reduce((sum,r)=>sum+r.quantity,0) || Number(b.rooms_count||0) || 1;
  const pmsBlocksCancellation = pmsBookingStatus === "checked_in" || pmsBookingStatus === "checked_out";
  const effectiveStatus = String(b.booking_status || "reserved").toLowerCase();
  const terminalStatus = ["cancelled","checked_out","no_show"].includes(effectiveStatus);
  const today = moment().startOf("day");
  const checkInDay = moment(b.check_in_date).startOf("day");
  const checkOutDay = moment(b.check_out_date).startOf("day");

  const unsyncedTransitions = {
    pending:    ["reserved","confirmed","cancelled"],
    reserved:   ["confirmed","checked_in","cancelled","no_show"],
    confirmed:  ["checked_in","cancelled","no_show"],
    checked_in: ["checked_out"],
  };

  const statusOptionsForBooking = BOOKING_STATUS_OPTIONS.map((option) => {
    if (option.value === effectiveStatus) return option;

    if (pmsIsSynced) {
      const cancellationAllowed = option.value === "cancelled" && !pmsBlocksCancellation && pmsBookingStatus !== "cancelled" && !terminalStatus;
      return cancellationAllowed
        ? option
        : { ...option, isDisabled:true, label:`${option.label} — managed in StayX` };
    }

    const allowed = (unsyncedTransitions[effectiveStatus] || []).includes(option.value);
    let disabled = !allowed;
    let suffix = !allowed ? " — not a valid next step" : "";

    if (option.value === "checked_in" && allowed && today.isBefore(checkInDay)) {
      disabled = true; suffix = " — available from check-in date";
    }
    if (option.value === "checked_in" && allowed && !today.isBefore(checkOutDay)) {
      disabled = true; suffix = " — stay dates elapsed";
    }
    if (option.value === "no_show" && allowed && today.isBefore(checkInDay)) {
      disabled = true; suffix = " — available from check-in date";
    }

    return disabled ? { ...option, isDisabled:true, label:`${option.label}${suffix}` } : option;
  });

  const appAccess = (()=>{
    try { const v=JSON.parse(localStorage.getItem("app_access")||"[]"); return Array.isArray(v)?v:[]; }
    catch { return []; }
  })();
  const companyHasHms = appAccess.includes("hms") && appAccess.includes("booking-engine");

  // Refund action eligibility
  const refundDone    = refSt==="processed" || refSt==="manual";
  const canRzpRefund  = isCancelled && isPaid && isRazorpay && !refundDone && b.payment_reference;
  const canManualRef  = isCancelled && isPaid && !refundDone;  // any payment method — staff overrides

  const authHeader = () => ({
    "Content-Type":"application/json",
    "Authorization":`Bearer ${localStorage.getItem("token")}`,
  });

  // ── Sync Booking Engine reservation to StayX Front Office ─────────────
  const syncToPms = async () => {
    if(pmsIsSynced || syncingPms) return;

    const confirm = await Swal.fire({
      icon:"question",
      title:"Sync to Front Office?",
      html:`<div style="text-align:left;font-size:13px;line-height:1.7;color:#475569">
        <div><b>${b.booking_number}</b> · ${b.guest_full_name||"Guest"}</div>
        <div>${fmtDate(b.check_in_date)} → ${fmtDate(b.check_out_date)} · ${b.nights||moment(b.check_out_date).diff(moment(b.check_in_date),"days")} night(s)</div>
        <div style="margin-top:8px">StayX will automatically allocate physical room number(s) from the exact booked room type for the requested dates.</div>
      </div>`,
      showCancelButton:true,
      confirmButtonText:"Sync reservation",
      cancelButtonText:"Cancel",
      confirmButtonColor:"#2563eb",
      reverseButtons:true,
    });
    if(!confirm.isConfirmed) return;

    setSP(true);
    try {
      const resp = await fetch(`${apiURL}/booking-engine/bookings/${b.id}/sync-pms`, {
        method:"POST", headers:authHeader(), body:JSON.stringify({}),
      });
      let data={};
      try { data=await resp.json(); } catch {}

      if(!resp.ok || !data.success) {
        await Swal.fire({
          icon:"error",
          title:"PMS sync failed",
          text:data.message||"The reservation could not be synced to StayX Front Office.",
          confirmButtonText:"OK",
        });
        return;
      }

      const nextSync = {
        is_synch:true,
        synch_at:data.synchAt||new Date().toISOString(),
        pms_booking_id:data.pmsBookingId||b.pms_booking_id||null,
        pms_booking_number:data.pmsBookingNumber||b.pms_booking_number||null,
      };
      setSS(nextSync);
      message.success(data.alreadySynced?"Reservation was already synced.":"Reservation synced to Front Office.");
      await onUpdated?.();
    } catch(err) {
      await Swal.fire({icon:"error",title:"PMS sync failed",text:"Unable to reach the server. Please try again.",confirmButtonText:"OK"});
    } finally {
      setSP(false);
    }
  };

  // ── Change booking status ──────────────────────────────────────────────
  const changeStatus = async () => {
    if(!newStatus || newStatus===b.booking_status) { setSM(false); return; }
    if(newStatus === "cancelled" && pmsBlocksCancellation) {
      message.error(`Cancellation is not allowed because the StayX reservation is already ${pmsBookingStatus === "checked_in" ? "checked in" : "checked out"}.`);
      return;
    }
    setCS(true);
    try {
      const resp = await fetch(`${apiURL}/booking-engine/update-booking-status`, {
        method:"POST", headers:authHeader(),
        body:JSON.stringify({ bookingNumber:b.booking_number, companyId:b.companyid||companyId, newStatus, note:statusNote }),
      });
      const data = await resp.json();
      if(data.success) {
        message.success(`Status changed to ${getS(newStatus).label}`);
        setSM(false); onUpdated(); onClose();
      } else if([
        "pms_checked_in","pms_checked_out","be_checked_in","be_checked_out",
        "pms_managed_status","stayx_access_required","invalid_status_transition",
        "terminal_booking_status","checkin_too_early","no_show_too_early",
        "stay_dates_elapsed","stay_already_completed","pms_booking_missing"
      ].includes(data.code)) {
        await Swal.fire({
          icon:data.code === "stayx_access_required" ? "info" : "warning",
          title:data.code === "pms_managed_status" ? "Managed in StayX" : data.code === "stayx_access_required" ? "StayX access required" : "Status change not allowed",
          text:data.message||"This status change is not valid for the current reservation state.",
          confirmButtonText:"OK",
        });
        setSM(false);
        await onUpdated?.();
      } else message.error(data.message||"Status change failed.");
    } catch { message.error("Failed to change status."); }
    finally { setCS(false); }
  };

  // ── Razorpay auto-refund ───────────────────────────────────────────────
  const initiateRzpRefund = async () => {
    const amt = Number(refundAmt);
    if(!amt || amt <= 0) { message.error("Enter a valid refund amount."); return; }
    if(amt > amtPaid)    { message.error(`Refund cannot exceed amount paid (${fmtMoney(amtPaid,cur)}).`); return; }
    setRL(true);
    try {
      const resp = await fetch(`${apiURL}/booking-engine/process-refund`, {
        method:"POST", headers:authHeader(),
        body:JSON.stringify({ bookingNumber:b.booking_number, companyId:b.companyid||companyId, overrideAmount:amt }),
      });
      const data = await resp.json();
      if(data.success) {
        message.success(`Refund of ${fmtMoney(data.refundAmount||amt,cur)} initiated.`);
        setRZM(false); onUpdated(); onClose();
      } else message.error(data.message||"Refund failed.");
    } catch { message.error("Failed to initiate refund."); }
    finally { setRL(false); }
  };

  // ── Manual refund (cash/bank/any) ──────────────────────────────────────
  const markManualRefund = async () => {
    const amt = Number(refundAmt);
    if(!amt || amt <= 0) { message.error("Enter a valid refund amount."); return; }
    setMR(true);
    try {
      const resp = await fetch(`${apiURL}/booking-engine/manual-refund`, {
        method:"POST", headers:authHeader(),
        body:JSON.stringify({ bookingNumber:b.booking_number, companyId:b.companyid||companyId, refundAmount:amt, refundNotes:refundNote||"Manual refund recorded by staff." }),
      });
      const data = await resp.json();
      if(data.success) {
        message.success("Refund marked as processed."); setRM(false); onUpdated(); onClose();
      } else message.error(data.message||"Failed.");
    } catch { message.error("Failed."); }
    finally { setMR(false); }
  };

  // Build timeline events from booking data
  const timeline = [
    b.created_at  && {label:"Booking created",         sub:`#${b.booking_number}`, time:fmtDateTime(b.created_at), done:true},
    isPaid        && {label:"Payment received",         sub:payLabel(b.payment_method), time:fmtDateTime(b.created_at), done:true},
    isCancelled   && {label:"Booking cancelled",        sub:b.cancellation_reason||"", time:fmtDateTime(b.cancelled_at), warn:true},
    b.refund_initiated_at && {label:"Refund initiated", sub:fmtMoney(refundAmt_,cur), time:fmtDateTime(b.refund_initiated_at), done:refSt==="processed"||refSt==="manual", warn:refSt==="failed"},
    b.refund_processed_at && refSt==="processed" && {label:"Refund completed", time:fmtDateTime(b.refund_processed_at), done:true},
    pmsSynchAt    && {label:"Synced to PMS",            sub:pmsBookingNumber?`PMS #${pmsBookingNumber}`:null, time:fmtDateTime(pmsSynchAt), done:true},
  ].filter(Boolean);

  const drawerStyle = {
    header: { borderBottom:"1px solid #dfe7f0", padding:"14px 20px", background:"#fff" },
    body:   { padding:"0", background:"#fff", overflowY:"auto" },
    content:{ boxShadow:"-10px 0 42px rgba(15,23,42,0.18)", borderRadius:"16px 0 0 16px", background:"#fff" },
  };

  return (<>
    <Drawer
      open={open} onClose={onClose} placement="right" closable={false}
      width={Math.min(window.innerWidth*0.82, 1100)}
      zIndex={1200}
      styles={drawerStyle}
      title={
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",gap:14,flexWrap:"wrap"}}>
          <div style={{flex:"1 1 440px",minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:9,flexWrap:"wrap"}}>
              <span style={{fontSize:18,fontWeight:900,color:"#0f172a",letterSpacing:"-0.02em"}}>{b.guest_full_name}</span>
              <span style={{fontSize:11.5,fontFamily:"monospace",color:"#64748b",background:"#f8fafc",
                border:"1px solid #e2e8f0",borderRadius:7,padding:"2px 8px",fontWeight:700}}>#{b.booking_number}</span>
              <Pill {...sm}/>
              {refSt!=="none" && <Pill {...refM} sm/>}
            </div>
            <div style={{fontSize:12.5,color:"#334155",marginTop:5,display:"flex",gap:13,flexWrap:"wrap",fontWeight:600}}>
              <span style={{display:"inline-flex",alignItems:"center"}}><Calendar size={12} style={{marginRight:5}}/>{fmtDate(b.check_in_date)} → {fmtDate(b.check_out_date)}</span>
              <span>· {b.nights||"?"} Nights</span>
              {b.guest_phone && <span style={{display:"inline-flex",alignItems:"center"}}><Phone size={12} style={{marginRight:5}}/>{b.guest_phone}</span>}
              {b.guest_email && <span style={{display:"inline-flex",alignItems:"center"}}><Mail size={12} style={{marginRight:5}}/>{b.guest_email}</span>}
            </div>
          </div>

          <div style={{display:"flex",alignItems:"center",gap:10,flex:"0 1 auto",minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:9,padding:"7px 11px",
              border:"1px solid #dbe3ee",borderRadius:11,background:"#fbfdff",minWidth:210,maxWidth:300}}>
              <div style={{width:30,height:30,borderRadius:9,background:"#eff6ff",display:"flex",alignItems:"center",justifyContent:"center",color:"#1d4ed8",flexShrink:0}}>
                <Home size={15}/>
              </div>
              <div style={{minWidth:0}}>
                <div style={{fontSize:10.5,fontWeight:800,color:"#334155",lineHeight:1.2}}>Room Type</div>
                <div style={{fontSize:13.5,fontWeight:900,color:"#0f172a",lineHeight:1.3,marginTop:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{bookedRoomLabel}</div>
                <div style={{fontSize:10.5,color:"#475569",marginTop:1,fontWeight:600}}>
                  {bookedRoomQty} Room{bookedRoomQty===1?"":"s"}{bookedRatePlan?` · ${bookedRatePlan}`:""}
                </div>
              </div>
            </div>

            <button onClick={()=>setSM(true)}
              style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:10,
                border:"1px solid #cfd9e5",background:"#fff",color:"#0f172a",
                fontSize:12.5,fontWeight:800,cursor:"pointer",whiteSpace:"nowrap",height:38}}>
              <Edit3 size={13}/> Change Status
            </button>
            <Tooltip title="Close">
              <button onClick={onClose} aria-label="Close booking details"
                style={{width:38,height:38,borderRadius:10,border:"1px solid #cfd9e5",background:"#fff",
                  color:"#0f172a",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
                <X size={18}/>
              </button>
            </Tooltip>
          </div>
        </div>
      }
    >
      {/* 3-column grid: left detail | center payment+refund | right timeline */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 300px",gap:0,height:"100%"}}>

        {/* ── COL 1: Guest · Stay · Billing ───────────────────────────── */}
        <div style={{padding:"18px 20px 28px 28px",overflowY:"auto",borderRight:"1px solid #e8eef4"}}>

          <DSection icon={<User size={13}/>} title="Guest"/>
          <DR label="Full name"   value={b.guest_full_name}/>
          <DR label="Email"       value={b.guest_email}     copy mono/>
          <DR label="Phone"       value={b.guest_phone}     copy/>
          <DR label="Country"     value={b.guest_country}/>
          <DR label="City"        value={b.guest_city}      last/>

          <DSection icon={<Calendar size={13}/>} title="Stay details"/>
          <DR label="Room type"    value={bookedRoomLabel} vs={{fontWeight:900,color:"#0f172a"}}/>
          {bookedRatePlan && <DR label="Rate plan" value={bookedRatePlan}/>}
          <DR label="Check-in"     value={fmtDate(b.check_in_date)}/>
          <DR label="Check-out"    value={fmtDate(b.check_out_date)}/>
          <DR label="Nights"       value={String(b.nights||moment(b.check_out_date).diff(moment(b.check_in_date),"days")||0)}/>
          <DR label="Rooms"        value={String(b.rooms_quantity||b.rooms_count||"?")}/>
          <DR label="Guests"       value={String(b.total_guests||"?")}/>
          <DR label="Booking type" value={b.booking_type}/>
          <DR label="Source"       value={b.booking_source||b.channel||"Booking Engine"} last/>

          <DSection icon="💰" title="Billing breakdown"/>
          <DR label="Room subtotal" value={fmtMoney(b.room_subtotal||0,cur)}/>
          {Number(b.tax_amount  ||0)>0 && <DR label="Tax"         value={fmtMoney(b.tax_amount,cur)}/>}
          {Number(b.fee_amount  ||0)>0 && <DR label="Service fee" value={fmtMoney(b.fee_amount,cur)}/>}
          {Number(b.discount_amount||0)>0 && <DR label="Discount" value={`-${fmtMoney(b.discount_amount,cur)}`} vs={{color:"#15803d"}}/>}
          {Number(b.coupon_discount||0)>0 && <DR label={b.coupon_code?`Coupon (${b.coupon_code})`:"Coupon"}
            value={`-${fmtMoney(b.coupon_discount,cur)}`} vs={{color:"#15803d"}}/>}
          <div style={{display:"flex",justifyContent:"space-between",padding:"12px 0 4px",borderTop:"1.5px solid #d8e2ec",marginTop:4}}>
            <span style={{fontSize:14,fontWeight:800,color:"#0f172a"}}>Grand Total</span>
            <span style={{fontSize:19,fontWeight:900,color:"#0f172a",letterSpacing:"-0.02em"}}>{fmtMoney(b.grand_total,cur)}</span>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #f0f4f8"}}>
            <span style={{fontSize:12.5,color:"#1e293b",fontWeight:700}}>Amount Paid</span>
            <span style={{fontSize:14,fontWeight:800,color:amtPaid>0?"#15803d":"#0f172a"}}>{fmtMoney(amtPaid,cur)}</span>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0 0"}}>
            <span style={{fontSize:12.5,color:"#1e293b",fontWeight:700}}>Balance Due</span>
            <span style={{fontSize:14,fontWeight:700,color:amtDue>0?"#b91c1c":"#15803d"}}>{fmtMoney(amtDue,cur)}</span>
          </div>
        </div>

        {/* ── COL 2: Payment · Refund · Cancellation ───────────────────── */}
        <div style={{padding:"18px 20px 28px",overflowY:"auto",borderRight:"1px solid #e8eef4"}}>

          <DSection icon={<DollarSign size={14}/>} title="Payment"/>
          <DR label="Status"      value={b.payment_status} vs={{textTransform:"capitalize"}}/>
          <DR label="Method"      value={payLabel(b.payment_method)}/>
          <DR label="Gateway"     value={b.payment_gateway||"—"}/>
          {b.payment_reference && <DR label="Payment ID"  value={b.payment_reference} mono copy/>}
          {b.payment_order_id  && <DR label="Order ID"    value={b.payment_order_id}  mono copy/>}
          <DR label="Currency"    value={cur} last/>

          {/* ── REFUND section ── */}
          <DSection icon="↩" title="Refund"/>
          <DR label="Refund status" chip value={<Pill {...refM}/>}/>
          {refundAmt_>0 && <DR label="Refund amount" value={fmtMoney(refundAmt_,cur)} vs={{color:"#15803d",fontWeight:700}}/>}
          {b.refund_number       && <DR label="Refund case" value={b.refund_number} mono copy/>}
          {b.refund_id           && <DR label="Gateway refund ID" value={b.refund_id} mono copy/>}
          {b.refund_initiated_at && <DR label="Initiated at"  value={fmtDateTime(b.refund_initiated_at)}/>}
          {b.refund_processed_at && <DR label="Processed at"  value={fmtDateTime(b.refund_processed_at)}/>}
          {b.refund_notes        && (
            <div style={{padding:"10px 0",fontSize:12,color:"#64748b",borderTop:"1px solid #f0f4f8",
              lineHeight:1.6,borderBottom:"1px solid #f0f4f8",marginBottom:2}}>
              📝 {b.refund_notes}
            </div>
          )}

          {/* Refund actions ── */}
          {refundDone ? (
            <div style={{marginTop:14,padding:"12px 14px",background:"#ecfdf5",borderRadius:12,
              border:"1px solid #a7f3d0",display:"flex",gap:8,alignItems:"flex-start"}}>
              <CheckCircle size={13} color="#15803d" style={{flexShrink:0,marginTop:1}}/>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"#14532d"}}>Refund completed</div>
                <div style={{fontSize:12,color:"#166534",marginTop:2}}>
                  {refSt==="manual"?"Marked as manually refunded by staff.":"Processed via Razorpay."}
                </div>
              </div>
            </div>
          ) : isCancelled && isPaid ? (
            <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:8}}>
              {/* Warning if needs action */}
              {(refSt==="pending"||refSt==="failed"||b.refund_requires_manual_action) && (
                <div style={{padding:"10px 14px",background:"#fffbeb",borderRadius:10,
                  border:"1px solid #fde68a",display:"flex",gap:8,alignItems:"flex-start"}}>
                  <AlertTriangle size={13} color="#d97706" style={{flexShrink:0,marginTop:1}}/>
                  <div style={{fontSize:12,color:"#78350f",lineHeight:1.5}}>
                    {refSt==="failed"?"Previous auto-refund failed. Please retry or process manually.":
                     refSt==="pending"?"Refund is being processed by Razorpay.":
                     "Manual action required — refund not yet processed."}
                  </div>
                </div>
              )}

              {/* Razorpay auto-refund */}
              {canRzpRefund && (
                <button onClick={()=>{ setRA(String(amtPaid)); setRZM(true); }}
                  style={{width:"100%",padding:"12px 16px",borderRadius:12,border:"none",
                    background:"linear-gradient(135deg,#1e40af,#1d4ed8)",color:"#fff",
                    fontSize:13,fontWeight:700,cursor:"pointer",
                    display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                    boxShadow:"0 4px 14px rgba(29,78,216,0.3)"}}>
                  <DollarSign size={15}/> Refund via Razorpay
                  <span style={{fontSize:11,opacity:0.85,marginLeft:2}}>({fmtMoney(amtPaid,cur)})</span>
                </button>
              )}

              {/* Manual refund — always available when cancelled + paid */}
              <button onClick={()=>{ setRA(String(amtPaid)); setRM(true); }}
                style={{width:"100%",padding:"12px 16px",borderRadius:12,
                  border:"1.5px solid #d97706",background:"#fffbeb",color:"#92400e",
                  fontSize:13,fontWeight:700,cursor:"pointer",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                <Edit3 size={13}/> Record Manual Refund
                {!isRazorpay && <span style={{fontSize:11,opacity:0.8}}>({payLabel(b.payment_method)})</span>}
              </button>
            </div>
          ) : !isCancelled && isPaid ? (
            <div style={{marginTop:10,padding:"10px 14px",background:"#f8fafc",borderRadius:10,
              border:"1px solid #e2e8f0",fontSize:12,color:"#94a3b8"}}>
              Refunds are available after booking is cancelled.
            </div>
          ) : null}

          {/* ── Cancellation details ── */}
          {isCancelled && (
            <>
              <DSection icon="🚫" title="Cancellation"/>
              <DR label="Cancelled on"   value={fmtDateTime(b.cancelled_at)}/>
              <DR label="Reason"         value={
                b.cancellation_reason
                  ? b.cancellation_reason.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())
                  : "Not specified"
              }/>
              {b.cancellation_policy && <DR label="Policy applied" value={b.cancellation_policy} last/>}
            </>
          )}

          <DSection icon="📋" title="Booking meta"/>
          <DR label="Created"       value={fmtDateTime(b.created_at)}/>
          <DR label="Last updated"  value={fmtDateTime(b.updated_at)}/>
          <div style={{padding:"11px 0",borderBottom:"1px solid #f0f4f8"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:14}}>
              <span style={{fontSize:12.5,color:"#1e293b",fontWeight:700,minWidth:130}}>PMS Sync</span>
              {pmsIsSynced ? (
                <div style={{textAlign:"right"}}>
                  <div style={{display:"inline-flex",alignItems:"center",gap:6,color:"#15803d",fontSize:13,fontWeight:800}}>
                    <CheckCircle size={13}/> Synced
                  </div>
                  <div style={{fontSize:11,color:"#94a3b8",marginTop:2}}>{fmtDateTime(pmsSynchAt)}</div>
                  {pmsBookingNumber && <div style={{fontSize:11,color:"#64748b",fontFamily:"monospace",marginTop:1}}>PMS #{pmsBookingNumber}</div>}
                </div>
              ) : (
                <div style={{display:"flex",alignItems:"center",gap:9}}>
                  <span style={{fontSize:12,fontWeight:700,color:"#d97706"}}>Pending sync</span>
                  {companyHasHms ? (
                    <Button size="small" type="primary" loading={syncingPms} onClick={syncToPms}
                      icon={!syncingPms?<RefreshCw size={12}/>:null}
                      style={{height:30,borderRadius:8,fontWeight:700,fontSize:12}}>
                      Sync
                    </Button>
                  ) : (
                    <Tooltip title="This property requires both Booking Engine and StayX Front Office access for PMS sync.">
                      <span style={{fontSize:11,color:"#94a3b8",border:"1px solid #e2e8f0",borderRadius:7,padding:"4px 7px"}}>HMS access required</span>
                    </Tooltip>
                  )}
                </div>
              )}
            </div>
          </div>
          {b.special_requests && <DR label="Special requests" value={b.special_requests}/>}
          <DR label="Booking ref"   value={b.reference_code||b.booking_number} mono copy last/>
        </div>

        {/* ── COL 3: Timeline ──────────────────────────────────────────── */}
        <div style={{padding:"18px 20px 28px",overflowY:"auto",background:"#fff"}}>
          <DSection icon="🕐" title="Timeline"/>
          {timeline.length ? timeline.map((t,i)=>(
            <TLItem key={i} {...t}/>
          )) : <div style={{fontSize:13,color:"#94a3b8",marginTop:8}}>No events yet.</div>}

          {/* Quick stats */}
          <div style={{marginTop:24,display:"flex",flexDirection:"column",gap:8}}>
            {[
              {label:"Booking total",  val:fmtMoney(b.grand_total,cur),   color:"#1e3a5f"},
              {label:"Paid",           val:fmtMoney(amtPaid,cur),          color:"#15803d"},
              {label:"Due",            val:fmtMoney(amtDue,cur),           color:amtDue>0?"#b91c1c":"#94a3b8"},
              refundAmt_>0 && {label:"Refunded", val:fmtMoney(refundAmt_,cur), color:"#7c3aed"},
            ].filter(Boolean).map(s=>(
              <div key={s.label} style={{display:"flex",justifyContent:"space-between",
                padding:"10px 14px",background:"#fff",borderRadius:10,
                border:"1px solid #e8eef4",boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
                <span style={{fontSize:12.5,color:"#1e293b",fontWeight:700}}>{titleCaseLabel(s.label)}</span>
                <span style={{fontSize:14,fontWeight:800,color:s.color}}>{s.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Drawer>

    {/* ── Change Status Modal ────────────────────────────────────────────── */}
    <Modal open={statusModal} onCancel={()=>setSM(false)} footer={null} centered width={440}
      zIndex={1300}
      title={<span style={{fontWeight:800,fontSize:16}}>Change booking status</span>}
      styles={{content:{borderRadius:16},header:{borderBottom:"1px solid #f0f4f8",padding:"18px 24px"},body:{padding:"20px 24px 24px"}}}>
      <div style={{marginBottom:16}}>
        <div style={{display:"grid",gridTemplateColumns:pmsIsSynced&&pmsBookingStatus?"1fr 1fr":"1fr",gap:10,marginBottom:14}}>
          <div style={{padding:"10px 12px",background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:10}}>
            <div style={{fontSize:10,fontWeight:800,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Booking Engine status</div>
            <div style={{display:"flex",alignItems:"center",gap:7}}>
              <span style={{width:7,height:7,borderRadius:"50%",background:getS(effectiveStatus).dot,flexShrink:0}}/>
              <strong style={{fontSize:13,color:getS(effectiveStatus).color}}>{getS(effectiveStatus).label}</strong>
            </div>
          </div>
          {pmsIsSynced && pmsBookingStatus ? (
            <div style={{padding:"10px 12px",background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:10}}>
              <div style={{fontSize:10,fontWeight:800,color:"#65a30d",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>StayX Front Office status</div>
              <div style={{display:"flex",alignItems:"center",gap:7}}>
                <span style={{width:7,height:7,borderRadius:"50%",background:getS(pmsBookingStatus).dot,flexShrink:0}}/>
                <strong style={{fontSize:13,color:getS(pmsBookingStatus).color}}>{getS(pmsBookingStatus).label}</strong>
              </div>
            </div>
          ) : null}
        </div>
        {pmsIsSynced ? (
          <div style={{padding:"14px 15px",background:"linear-gradient(135deg,#eff6ff 0%,#f8fbff 100%)",border:"1px solid #bfdbfe",borderRadius:12,marginBottom:16,color:"#1e40af",display:"flex",gap:11,alignItems:"flex-start"}}>
            <div style={{width:30,height:30,borderRadius:9,background:"#dbeafe",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <Globe size={15} color="#2563eb"/>
            </div>
            <div style={{minWidth:0}}>
              <div style={{fontSize:10,fontWeight:900,color:"#2563eb",letterSpacing:"0.09em",textTransform:"uppercase",marginBottom:3}}>Status control</div>
              <div style={{fontSize:13,fontWeight:800,color:"#1e3a8a",marginBottom:3}}>Managed by StayX Front Office</div>
              <div style={{fontSize:12,color:"#475569",lineHeight:1.55}}>
                This reservation is linked to StayX. Check-in, check-out, no-show and completion must be performed in Front Office; Booking Engine mirrors those operational states automatically. Cancellation can be requested here only while the stay is still cancellable and will be coordinated across both systems.
              </div>
            </div>
          </div>
        ) : (
          <div style={{padding:"14px 15px",background:"linear-gradient(135deg,#f8fafc 0%,#ffffff 100%)",border:"1px solid #e2e8f0",borderRadius:12,marginBottom:16,color:"#475569",display:"flex",gap:11,alignItems:"flex-start"}}>
            <div style={{width:30,height:30,borderRadius:9,background:"#f1f5f9",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <Edit3 size={14} color="#475569"/>
            </div>
            <div style={{minWidth:0}}>
              <div style={{fontSize:10,fontWeight:900,color:"#64748b",letterSpacing:"0.09em",textTransform:"uppercase",marginBottom:3}}>Status control</div>
              <div style={{fontSize:13,fontWeight:800,color:"#0f172a",marginBottom:3}}>Managed in Booking Engine</div>
              <div style={{fontSize:12,color:"#64748b",lineHeight:1.55}}>
                No StayX reservation is linked. You can progress this booking here through the valid next lifecycle step. Completed, cancelled and no-show bookings remain terminal and cannot be reopened.
              </div>
            </div>
          </div>
        )}
        <label style={{fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.04em"}}>
          Next booking status
        </label>
        <Select
          options={statusOptionsForBooking}
          value={statusOptionsForBooking.find(o=>o.value===newStatus)||null}
          isOptionDisabled={option=>!!option.isDisabled}
          onChange={o=>setNS(o?.value||"")}
          placeholder="Select status"
          styles={{control:s=>({...s,borderRadius:8,borderColor:"#e2e8f0",minHeight:40}),
                   menu:s=>({...s,zIndex:1400}),menuPortal:s=>({...s,zIndex:1400})}}
          menuPortalTarget={document.body}
        />
        <label style={{fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:6,marginTop:14,textTransform:"uppercase",letterSpacing:"0.04em"}}>
          Note (optional)
        </label>
        <textarea rows={2} value={statusNote} onChange={e=>setSN(e.target.value)}
          placeholder="Reason for status change…"
          style={{width:"100%",padding:"10px 12px",borderRadius:8,border:"1.5px solid #e2e8f0",fontSize:13,resize:"vertical",outline:"none"}}/>
        {pmsBlocksCancellation && (
          <div style={{marginTop:10,padding:"10px 14px",background:"#fff7ed",borderRadius:8,
            border:"1px solid #fed7aa",fontSize:12,color:"#9a3412",display:"flex",gap:7}}>
            <AlertTriangle size={13} style={{flexShrink:0,marginTop:1}}/>
            StayX status is <strong>{pmsBookingStatus === "checked_in" ? "Checked In" : "Checked Out"}</strong>. Cancellation is locked across Booking Engine and Front Office.
          </div>
        )}
        {newStatus==="cancelled" && !pmsBlocksCancellation && (
          <div style={{marginTop:10,padding:"10px 14px",background:"#fef2f2",borderRadius:8,
            border:"1px solid #fecaca",fontSize:12,color:"#7f1d1d",display:"flex",gap:7}}>
            <AlertTriangle size={13} style={{flexShrink:0,marginTop:1}}/>
            {pmsIsSynced
              ? "This will cancel both Booking Engine and the linked StayX reservation. Paid online bookings will then enter the existing refund workflow."
              : "This will cancel the Booking Engine reservation. Paid online bookings will then enter the existing refund workflow."}
          </div>
        )}
        {pmsIsSynced && !companyHasHms && (
          <div style={{marginTop:10,padding:"10px 14px",background:"#fff7ed",borderRadius:8,border:"1px solid #fed7aa",fontSize:12,color:"#9a3412",display:"flex",gap:7}}>
            <AlertTriangle size={13} style={{flexShrink:0,marginTop:1}}/>
            StayX is not enabled for this property account. This already-synced reservation is protected from lifecycle changes until Front Office access is restored.
          </div>
        )}
      </div>
      <div style={{display:"flex",gap:10}}>
        <button onClick={()=>setSM(false)}
          style={{flex:1,padding:"12px",borderRadius:10,border:"1px solid #e2e8f0",
            background:"#fff",color:"#374151",fontSize:14,fontWeight:700,cursor:"pointer"}}>
          Cancel
        </button>
        <button onClick={changeStatus} disabled={changingStatus||!newStatus||newStatus===effectiveStatus||(newStatus==="cancelled"&&pmsBlocksCancellation)||(pmsIsSynced&&!companyHasHms)||!!statusOptionsForBooking.find(o=>o.value===newStatus)?.isDisabled}
          style={{flex:2,padding:"12px",borderRadius:10,border:"none",
            background:changingStatus||!newStatus||newStatus===effectiveStatus||(newStatus==="cancelled"&&pmsBlocksCancellation)||(pmsIsSynced&&!companyHasHms)||!!statusOptionsForBooking.find(o=>o.value===newStatus)?.isDisabled?"#bfdbfe":"#1d4ed8",
            color:"#fff",fontSize:14,fontWeight:700,cursor:changingStatus?"not-allowed":"pointer",
            display:"flex",alignItems:"center",justifyContent:"center",gap:7}}>
          {changingStatus?<><RefreshCw size={13} className="spin"/> Saving…</>:<><CheckCircle size={14}/> Confirm change</>}
        </button>
      </div>
    </Modal>

    {/* ── Razorpay Refund Modal (editable amount) ────────────────────────── */}
    <Modal open={rzpRefundModal} onCancel={()=>setRZM(false)} footer={null} centered width={440}
      zIndex={1300}
      title={<span style={{fontWeight:800,fontSize:16}}>Refund via Razorpay</span>}
      styles={{content:{borderRadius:16},header:{borderBottom:"1px solid #f0f4f8",padding:"18px 24px"},body:{padding:"20px 24px 24px"}}}>
      <div style={{marginBottom:16}}>
        <div style={{padding:"10px 14px",background:"#eff6ff",borderRadius:10,border:"1px solid #bfdbfe",
          fontSize:12,color:"#1e40af",marginBottom:14}}>
          Payment ID: <strong>{b.payment_reference}</strong>
          <br/>Amount paid: <strong>{fmtMoney(amtPaid,cur)}</strong>
          <br/>You can refund the full amount or a partial amount.
        </div>
        <label style={{fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.04em"}}>
          Refund Amount ({cur})
        </label>
        <input type="number" min="0.01" max={amtPaid} step="0.01" value={refundAmt}
          onChange={e=>setRA(e.target.value)}
          style={{width:"100%",padding:"11px 14px",borderRadius:8,border:"1.5px solid #e2e8f0",
            fontSize:16,fontWeight:800,outline:"none"}}
          placeholder="0.00"/>
        <div style={{display:"flex",gap:8,marginTop:8}}>
          <button onClick={()=>setRA(String(amtPaid))}
            style={{padding:"5px 12px",borderRadius:6,border:"1px solid #bfdbfe",background:"#eff6ff",
              color:"#1e40af",fontSize:12,fontWeight:700,cursor:"pointer"}}>
            Full amount
          </button>
          <button onClick={()=>setRA(String((amtPaid/2).toFixed(2)))}
            style={{padding:"5px 12px",borderRadius:6,border:"1px solid #e2e8f0",background:"#fff",
              color:"#475569",fontSize:12,fontWeight:700,cursor:"pointer"}}>
            50%
          </button>
        </div>
      </div>
      <div style={{display:"flex",gap:10}}>
        <button onClick={()=>setRZM(false)}
          style={{flex:1,padding:"12px",borderRadius:10,border:"1px solid #e2e8f0",
            background:"#fff",color:"#374151",fontSize:14,fontWeight:700,cursor:"pointer"}}>
          Cancel
        </button>
        <button onClick={initiateRzpRefund} disabled={refundLoading}
          style={{flex:2,padding:"12px",borderRadius:10,border:"none",
            background:refundLoading?"#bfdbfe":"#1d4ed8",color:"#fff",
            fontSize:14,fontWeight:700,cursor:refundLoading?"not-allowed":"pointer",
            display:"flex",alignItems:"center",justifyContent:"center",gap:7}}>
          {refundLoading?<><RefreshCw size={13} className="spin"/> Processing…</>:<><DollarSign size={13}/> Process Refund</>}
        </button>
      </div>
    </Modal>

    {/* ── Manual Refund Modal ────────────────────────────────────────────── */}
    <Modal open={refundModal} onCancel={()=>setRM(false)} footer={null} centered width={440}
      zIndex={1300}
      title={<span style={{fontWeight:800,fontSize:16}}>Record Manual Refund</span>}
      styles={{content:{borderRadius:16},header:{borderBottom:"1px solid #f0f4f8",padding:"18px 24px"},body:{padding:"20px 24px 24px"}}}>
      <div style={{marginBottom:16}}>
        <div style={{padding:"10px 14px",background:"#fffbeb",borderRadius:10,border:"1px solid #fde68a",
          fontSize:12,color:"#92400e",marginBottom:14,lineHeight:1.5}}>
          Payment method: <strong>{payLabel(b.payment_method)||"Not specified"}</strong>
          <br/>Amount paid: <strong>{fmtMoney(amtPaid,cur)}</strong>
          <br/>Enter the amount you have physically returned to the guest.
        </div>
        <label style={{fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.04em"}}>
          Refund Amount ({cur})
        </label>
        <input type="number" min="0.01" step="0.01" value={refundAmt}
          onChange={e=>setRA(e.target.value)}
          style={{width:"100%",padding:"11px 14px",borderRadius:8,border:"1.5px solid #e2e8f0",
            fontSize:16,fontWeight:800,outline:"none",marginBottom:14}}
          placeholder="0.00"/>
        <label style={{fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.04em"}}>
          Note (optional)
        </label>
        <textarea rows={3} value={refundNote} onChange={e=>setRN(e.target.value)}
          placeholder="e.g. Returned cash on 23 Mar 2026 at front desk"
          style={{width:"100%",padding:"10px 12px",borderRadius:8,border:"1.5px solid #e2e8f0",
            fontSize:13,resize:"vertical",outline:"none"}}/>
      </div>
      <div style={{display:"flex",gap:10}}>
        <button onClick={()=>setRM(false)}
          style={{flex:1,padding:"12px",borderRadius:10,border:"1px solid #e2e8f0",
            background:"#fff",color:"#374151",fontSize:14,fontWeight:700,cursor:"pointer"}}>
          Cancel
        </button>
        <button onClick={markManualRefund} disabled={markingRefund}
          style={{flex:2,padding:"12px",borderRadius:10,border:"none",
            background:markingRefund?"#fde68a":"#d97706",color:markingRefund?"#92400e":"#fff",
            fontSize:14,fontWeight:700,cursor:markingRefund?"not-allowed":"pointer",
            display:"flex",alignItems:"center",justifyContent:"center",gap:7}}>
          {markingRefund?<><RefreshCw size={13} className="spin"/> Saving…</>:<><CheckCircle size={14}/> Confirm Refund</>}
        </button>
      </div>
    </Modal>
  </>);
};

// ─── MAIN LIST COMPONENT ───────────────────────────────────────────────────
const BeBookingList = () => {
  const { Search } = Input;
  const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";
  const businessMoment = moment().startOf("day");

  const [dateRange, setDateRange] = useState({
    startDate: businessMoment.clone().startOf("month"),
    endDate:   businessMoment.clone(),
  });
  const [searchText,      setST] = useState("");
  const [statusFilter,    setSF] = useState(null);
  const [refundFilter,    setRF] = useState(null);
  const [payFilter,       setPF] = useState(null);
  const [bookings,        setBK] = useState([]);
  const [loading,         setLD] = useState(true);
  const [error,           setER] = useState(null);
  const [filterOpen,      setFO] = useState(false);
  const [drawerOpen,      setDO] = useState(false);
  const [selBooking,      setSB] = useState(null);
  const [isFullScreen,    setFS] = useState(false);
  const [pdfVisible,      setPV] = useState(false);
  const maximizeBtnRef = useRef(null);
  const companyId = Number(localStorage.getItem("company_id")) || undefined;

  const { getFullname } = UserService();
  const { getPartnerName } = PartnerService();

  useEffect(() => {
    const btn = maximizeBtnRef.current;
    const toggle = () => {
      if(!document.fullscreenElement) { document.documentElement.requestFullscreen().catch(()=>{}); setFS(true); }
      else { document.exitFullscreen().catch(()=>{}); setFS(false); }
    };
    const onFsc = () => setFS(!!document.fullscreenElement);
    if(btn) btn.addEventListener("click",toggle);
    document.addEventListener("fullscreenchange",onFsc);
    return ()=>{ if(btn)btn.removeEventListener("click",toggle); document.removeEventListener("fullscreenchange",onFsc); };
  },[]);

  const fetchBookings = useCallback(async()=>{
    if(!dateRange.startDate.isValid()||!dateRange.endDate.isValid()) return;
    const token = localStorage.getItem("token");
    if(!token) { message.error("Not logged in."); return; }
    setLD(true); setER(null);
    try {
      const p = new URLSearchParams({ startDate:dateRange.startDate.format("YYYY-MM-DD"), endDate:dateRange.endDate.format("YYYY-MM-DD") });
      if(companyId) p.append("companyId",String(companyId));
      const r = await fetch(`${apiURL}/booking-engine/booking-list?${p}`,{headers:{Authorization:`Bearer ${token}`}});
      if(!r.ok) throw new Error("Failed to fetch bookings.");
      const d = await r.json();
      if(d.success) setBK([...(d.bookings||[])].sort((a,b)=>new Date(b.created_at||0)-new Date(a.created_at||0)));
      else throw new Error(d.message||"No bookings found.");
    } catch(err) { setER(err.message); Swal.fire({title:"Error!",text:err.message,icon:"error"}); }
    finally { setLD(false); }
  },[dateRange,apiURL,companyId]);

  useEffect(()=>{ fetchBookings(); },[fetchBookings]);

  const handleDateChange = p => {
    setDateRange(prev=>{
      if(p==="prev") return {startDate:prev.startDate.clone().subtract(1,"month").startOf("month"),endDate:prev.startDate.clone().subtract(1,"month").endOf("month")};
      if(p==="next") return {startDate:prev.startDate.clone().add(1,"month").startOf("month"),endDate:prev.startDate.clone().add(1,"month").endOf("month")};
      const s=moment(p.start).startOf("day"),e=moment(p.end).startOf("day");
      return s.isAfter(e)?prev:{startDate:s,endDate:e};
    });
  };

  const statusOptions  = BOOKING_STATUS_OPTIONS;
  const refundOptions  = [{value:"none",label:"No Refund"},{value:"pending",label:"Refund Pending"},{value:"processed",label:"Refunded"},{value:"failed",label:"Refund Failed"},{value:"manual",label:"Manually Refunded"},{value:"not_applicable",label:"Manual Required"}];
  const payOptions     = [{value:"razorpay",label:"Razorpay"},{value:"cash",label:"Cash"},{value:"bank_transfer",label:"Bank Transfer"},{value:"upi",label:"UPI"},{value:"card",label:"Card"},{value:"netbanking",label:"Net Banking"},{value:"wallet",label:"Wallet"}];
  const activeFilters  = [statusFilter,refundFilter,payFilter].filter(Boolean).length;

  const filtered = bookings.filter(b=>{
    const s=(searchText||"").toLowerCase();
    const mS=!s||(b.booking_number||"").toLowerCase().includes(s)||(b.guest_full_name||"").toLowerCase().includes(s)||(b.guest_email||"").toLowerCase().includes(s)||(b.guest_phone||"").toLowerCase().includes(s)||roomTypeLabel(b).toLowerCase().includes(s);
    const mSt=!statusFilter||(b.booking_status||"").toLowerCase()===statusFilter.value;
    const mR=!refundFilter||(b.refund_status||"none").toLowerCase()===refundFilter.value;
    const mP=!payFilter||(b.payment_method||"").toLowerCase()===payFilter.value;
    return mS&&mSt&&mR&&mP;
  });

  // Summary stats
  const cur0         = bookings[0]?.currency_code||"INR";
  const totalRev     = filtered.reduce((s,b)=>s+Number(b.grand_total||0),0);
  const totalPaid    = filtered.reduce((s,b)=>s+Number(b.amount_paid||0),0);
  const totalDue     = filtered.reduce((s,b)=>s+Number(b.amount_due||0),0);
  const totalRefund  = filtered.reduce((s,b)=>s+Number(b.refund_amount||0),0);
  const cancelledCt  = filtered.filter(b=>(b.booking_status||"").toLowerCase()==="cancelled").length;
  const pendingRefCt = filtered.filter(b=>(b.refund_status||"").toLowerCase()==="pending").length;
  const failedRefCt  = filtered.filter(b=>(b.refund_status||"").toLowerCase()==="failed").length;

  // Exports
  const exportHeaders = [
    {key:"booking_number",title:"Booking No"},{key:"booking_status",title:"Status"},
    {key:"refund_status",title:"Refund Status"},{key:"guest_full_name",title:"Guest"},{key:"room_type",title:"Room Type"},
    {key:"guest_phone",title:"Phone"},{key:"guest_email",title:"Email"},
    {key:"check_in_date",title:"Check-In"},{key:"check_out_date",title:"Check-Out"},
    {key:"nights",title:"Nights"},{key:"grand_total",title:"Grand Total"},
    {key:"amount_paid",title:"Paid"},{key:"amount_due",title:"Due"},
    {key:"refund_amount",title:"Refund Amount"},{key:"payment_method",title:"Payment"},
    {key:"booking_type",title:"Type"},{key:"created_at",title:"Created"},
  ];
  const exportData = filtered.map(b=>({
    booking_number:b.booking_number, booking_status:(b.booking_status||"").toUpperCase(),
    refund_status:(b.refund_status||"none").toUpperCase(), guest_full_name:b.guest_full_name, room_type:roomTypeLabel(b),
    guest_phone:b.guest_phone||"", guest_email:b.guest_email||"",
    check_in_date:fmtDate(b.check_in_date), check_out_date:fmtDate(b.check_out_date),
    nights:b.nights||0, grand_total:fmtMoney(b.grand_total,b.currency_code),
    amount_paid:fmtMoney(b.amount_paid,b.currency_code), amount_due:fmtMoney(b.amount_due,b.currency_code),
    refund_amount:Number(b.refund_amount||0)>0?fmtMoney(b.refund_amount,b.currency_code):"—",
    payment_method:payLabel(b.payment_method), booking_type:b.booking_type,
    created_at:moment(b.created_at).format("YYYY-MM-DD HH:mm"),
  }));
  const totals = [{key:"Total",value:filtered.length},{key:"Revenue",value:totalRev.toFixed(2)},{key:"Paid",value:totalPaid.toFixed(2)},{key:"Due",value:totalDue.toFixed(2)},{key:"Refunded",value:totalRefund.toFixed(2)}];
  const sfx    = `_${moment().format("YYYYMMDD")}`;
  const dlMenu = (
    <Menu>
      <Menu.Item onClick={()=>exportService.exportToCSV(exportData,exportHeaders,`be_bookings${sfx}.csv`)}>Download CSV</Menu.Item>
      <Menu.Item onClick={()=>exportService.exportToXLS(exportData,exportHeaders,`be_bookings${sfx}.xlsx`)}>Download XLS</Menu.Item>
      <Menu.Item onClick={()=>exportService.exportToPDF(exportData,exportHeaders,"BE Bookings",`be_bookings${sfx}.pdf`,false,true,totals,totals)}>Download PDF</Menu.Item>
      <Menu.Item onClick={async()=>{ setLD(true); try{ const bl=await exportService.exportToPDF(exportData,exportHeaders,"BE Bookings",null,true,true,totals,totals); setPV(true); }catch{} finally{setLD(false);} }}>Print</Menu.Item>
    </Menu>
  );

  // ── Columns ──────────────────────────────────────────────────────────────
  const columns = [
    {
      title:"", dataIndex:"is_synch", width:40, fixed:"left",
      render:(v,r)=>(
        <Tooltip title={v?`Synced ${fmtDateTime(r.synch_at)}`:"Not synced"}>
          <span style={{fontSize:14,cursor:"default"}}>{v?"✅":"🔄"}</span>
        </Tooltip>
      ),
    },
    {
      title:"Guest", dataIndex:"guest_full_name", width:200, fixed:"left",
      sorter:(a,b)=>(a.guest_full_name||"").localeCompare(b.guest_full_name||""),
      render:(name,r)=>(
        <div style={{cursor:"pointer"}} onClick={()=>{setSB(r);setDO(true);}}>
          <div style={{fontWeight:700,color:"#0f172a",fontSize:13,lineHeight:1.3}}>{name}</div>
          <div style={{fontSize:11,color:"#94a3b8",display:"flex",gap:4,marginTop:2,alignItems:"center"}}>
            <Phone size={10}/> {r.guest_phone||"—"}
          </div>
        </div>
      ),
    },
    {
      title:"Room Type", key:"room_type", width:210, fixed:"left",
      sorter:(a,b)=>roomTypeLabel(a).localeCompare(roomTypeLabel(b)),
      render:(_,r)=>{
        const lines=roomTypeLines(r);
        const first=lines[0];
        const totalQty=Number(r.rooms_quantity||0)||lines.reduce((sum,x)=>sum+x.quantity,0)||Number(r.rooms_count||0)||1;
        return (
          <div style={{cursor:"pointer",minWidth:0}} onClick={()=>{setSB(r);setDO(true);}}>
            <div style={{fontSize:13,fontWeight:800,color:"#0f172a",lineHeight:1.3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}
              title={roomTypeLabel(r)}>{first?.name||r.room_type_names||"Room type unavailable"}</div>
            <div style={{fontSize:10.5,color:"#64748b",marginTop:3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
              {totalQty} room{totalQty===1?"":"s"}{lines.length>1?` · +${lines.length-1} more type${lines.length-1===1?"":"s"}`:""}{first?.ratePlan?` · ${first.ratePlan}`:""}
            </div>
          </div>
        );
      },
    },
    {
      title:"Booking No", dataIndex:"booking_number", width:155, fixed:"left",
      render:(bn,r)=>(
        // OPEN DRAWER — no navigation
        <button onClick={()=>{setSB(r);setDO(true);}}
          style={{background:"none",border:"none",cursor:"pointer",padding:0,
            fontFamily:"monospace",fontSize:12,color:"#1d4ed8",fontWeight:700,
            textDecoration:"underline",textDecorationStyle:"dotted"}}>
          {bn}
        </button>
      ),
    },
    {
      title:"Status", dataIndex:"booking_status", width:135, fixed:"left",
      render:s=>{ const m=getS(s); return <Pill {...m}/>; },
    },
    {
      title:"Check-In",  dataIndex:"check_in_date",  width:120,
      sorter:(a,b)=>new Date(a.check_in_date)-new Date(b.check_in_date),
      render:d=><span style={{fontSize:13,color:"#334155",fontWeight:500}}>{fmtDate(d)}</span>,
    },
    {
      title:"Check-Out", dataIndex:"check_out_date", width:120,
      sorter:(a,b)=>new Date(a.check_out_date)-new Date(b.check_out_date),
      render:d=><span style={{fontSize:13,color:"#334155",fontWeight:500}}>{fmtDate(d)}</span>,
    },
    {
      title:"N", dataIndex:"nights", width:52,
      render:(n,r)=>{
        const ct=n||moment(r.check_out_date).diff(moment(r.check_in_date),"days")||0;
        return <span style={{fontWeight:700,color:"#475569",fontSize:13}}>{ct}</span>;
      },
    },
    {
      title:"Grand Total", dataIndex:"grand_total", width:130,
      sorter:(a,b)=>Number(a.grand_total||0)-Number(b.grand_total||0),
      render:(v,r)=><span style={{fontWeight:800,color:"#1e3a5f",fontSize:13}}>{fmtMoney(v,r.currency_code)}</span>,
    },
    {
      title:"Paid", dataIndex:"amount_paid", width:115,
      render:(v,r)=>{
        const p=Number(v||0),d=Number(r.amount_due||0);
        return <span style={{fontWeight:700,fontSize:12,color:p>0&&d<=0?"#15803d":d>0?"#b45309":"#94a3b8"}}>{fmtMoney(v,r.currency_code)}</span>;
      },
    },
    {
      title:"Due", dataIndex:"amount_due", width:110,
      render:(v,r)=>{
        const d=Number(v||0);
        return <span style={{fontWeight:700,fontSize:12,color:d>0?"#b91c1c":"#94a3b8"}}>{d>0?fmtMoney(v,r.currency_code):"—"}</span>;
      },
    },
    {
      title:"Payment", dataIndex:"payment_method", width:110,
      render:m=><span style={{fontSize:12,color:"#475569"}}>{payLabel(m)}</span>,
    },
    {
      title:"Refund", dataIndex:"refund_status", width:145,
      render:(r,rec)=>{
        const m=getR(r);
        if(!r||r==="none") return <span style={{color:"#cbd5e1",fontSize:12}}>—</span>;
        return (
          <div>
            <Pill {...m} sm/>
            {Number(rec.refund_amount||0)>0 && (
              <div style={{fontSize:10,color:"#94a3b8",marginTop:2}}>{fmtMoney(rec.refund_amount,rec.currency_code)}</div>
            )}
          </div>
        );
      },
    },
    {
      title:"Created", dataIndex:"created_at", width:115, defaultSortOrder:"descend",
      sorter:(a,b)=>new Date(a.created_at)-new Date(b.created_at),
      render:d=><span style={{fontSize:11,color:"#94a3b8"}}>{fmtDate(d)}</span>,
    },
    {
      title:"", key:"act", width:48, fixed:"right",
      render:(_,r)=>(
        <Tooltip title="View & manage">
          <button onClick={()=>{setSB(r);setDO(true);}}
            style={{width:30,height:30,borderRadius:8,border:"1px solid #e2e8f0",
              background:"#fff",cursor:"pointer",display:"flex",alignItems:"center",
              justifyContent:"center",color:"#1d4ed8",fontSize:13}}>
            <Eye size={13}/>
          </button>
        </Tooltip>
      ),
    },
  ];

  const SBar = ({label,value,sub,accent,warn}) => (
    <div style={{background:"#fff",borderRadius:12,border:`1px solid ${warn?"#fde68a":"#e8eef4"}`,
      padding:"12px 16px",flex:"1 1 130px",minWidth:0,
      boxShadow:warn?"0 0 0 3px #fef9c3":"0 1px 4px rgba(0,0,0,0.04)"}}>
      <div style={{fontSize:10,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:2}}>{label}</div>
      <div style={{fontSize:20,fontWeight:900,color:accent||"#0f172a",letterSpacing:"-0.02em",lineHeight:1.1}}>{value}</div>
      {sub&&<div style={{fontSize:10,color:"#94a3b8",marginTop:2}}>{sub}</div>}
    </div>
  );

  return (
    <div className="page-wrapper-full">
      <div className="content container-fluid">

        {/* ── Header ── */}
        <div className="page-header page-top-icons" style={{paddingBottom:12}}>
          <div className="row align-items-center">
            <div className="col-md-2">
              <h3 className="page-title" style={{fontWeight:900,letterSpacing:"-0.02em",margin:0}}>Bookings</h3>
            </div>
            <div className="col-md-10">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <DatePicker
                  initialSettings={{
                    singleDatePicker:false,showDropdowns:true,
                    startDate:dateRange.startDate.toDate(),endDate:dateRange.endDate.toDate(),
                    ranges:{"Today":[businessMoment.toDate(),businessMoment.toDate()],"This Month":[businessMoment.clone().startOf("month").toDate(),businessMoment.clone().endOf("month").toDate()],"Last Month":[businessMoment.clone().subtract(1,"month").startOf("month").toDate(),businessMoment.clone().subtract(1,"month").endOf("month").toDate()],"Last 90 Days":[businessMoment.clone().subtract(90,"days").toDate(),businessMoment.toDate()],"This Year":[businessMoment.clone().startOf("year").toDate(),businessMoment.toDate()]},
                    locale:{format:"DD MMM YYYY",applyLabel:"Select",cancelLabel:"Cancel"},
                  }}
                  onCallback={(s,e)=>handleDateChange({start:s,end:e})}
                >
                  <div className="input-group" style={{width:248}}>
                    <input type="text" className="form-control" readOnly
                      value={`${dateRange.startDate.format("DD MMM YYYY")} – ${dateRange.endDate.format("DD MMM YYYY")}`}
                      style={{fontSize:13,fontWeight:700,textAlign:"center",cursor:"pointer",borderRight:"none",height:38,borderRadius:"6px 0 0 6px"}}/>
                    <span className="input-group-text" style={{height:38,background:"#fff",borderLeft:"none",borderRadius:"0 6px 6px 0",cursor:"pointer"}}>
                      <i className="fa fa-chevron-down" style={{color:"#64748b",fontSize:10}}/>
                    </span>
                  </div>
                </DatePicker>

                <div style={{flexGrow:1,maxWidth:380}}>
                  <Search placeholder="Name, room type, email, phone, booking no…" allowClear size="large"
                    onChange={e=>setST(e.target.value)} style={{borderRadius:8}}/>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <button onClick={()=>setFO(true)}
                    style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",
                      borderRadius:8,border:`1.5px solid ${activeFilters>0?"#1d4ed8":"#e2e8f0"}`,
                      background:activeFilters>0?"#eff6ff":"#fff",
                      color:activeFilters>0?"#1d4ed8":"#475569",
                      fontSize:13,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                    <Filter size={13}/> Filters
                    {activeFilters>0&&<span style={{background:"#1d4ed8",color:"#fff",borderRadius:20,padding:"0 7px",fontSize:11,fontWeight:800}}>{activeFilters}</span>}
                  </button>
                  <Tooltip title={isFullScreen?"Exit fullscreen":"Fullscreen"}>
                    <button ref={maximizeBtnRef}
                      style={{width:36,height:36,borderRadius:8,border:"1px solid #e2e8f0",background:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#475569",fontSize:16}}>
                      {isFullScreen?<Minimize2 size={15}/>:<Maximize2 size={15}/>}
                    </button>
                  </Tooltip>
                  <Tooltip title="Refresh">
                    <button onClick={fetchBookings}
                      style={{width:36,height:36,borderRadius:8,border:"1px solid #e2e8f0",background:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#475569",fontSize:16}}>
                      <RefreshCw size={14}/>
                    </button>
                  </Tooltip>
                  <Dropdown overlay={dlMenu} trigger={["click"]} placement="bottomRight">
                    <Button type="default" style={{display:"flex",alignItems:"center",gap:6,borderRadius:8,fontWeight:600,height:36}}>
                      <i className="la la-download"/> Download <ChevronDown size={12}/>
                    </Button>
                  </Dropdown>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats bar ── */}
        {!loading && filtered.length>0 && (
          <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
            <SBar label="Bookings"       value={filtered.length}               accent="#0f172a"/>
            <SBar label="Revenue"        value={fmtMoney(totalRev,cur0)}       accent="#1e3a5f"/>
            <SBar label="Collected"      value={fmtMoney(totalPaid,cur0)}      accent="#15803d" sub="amount paid"/>
            <SBar label="Outstanding"    value={fmtMoney(totalDue,cur0)}       accent={totalDue>0?"#b91c1c":"#94a3b8"} sub="amount due"/>
            {totalRefund>0&&<SBar label="Total Refunded" value={fmtMoney(totalRefund,cur0)} accent="#7c3aed" sub="this period"/>}
            <SBar label="Cancellations"  value={cancelledCt}                   accent={cancelledCt>0?"#b91c1c":"#94a3b8"}/>
            {pendingRefCt>0&&<SBar label="Refunds Pending" value={pendingRefCt} accent="#d97706" warn sub="need action"/>}
            {failedRefCt>0 &&<SBar label="Refunds Failed"  value={failedRefCt}  accent="#b91c1c" warn sub="need retry"/>}
          </div>
        )}

        <PrintReportPreview visible={pdfVisible} onClose={()=>setPV(false)}
          reportTitle="Booking Engine Bookings" propertyName="Your Property"
          data={exportData} headers={exportHeaders} footerData={totals}
          totalColumns={["nights","grand_total"]}
          onDownloadPDF={()=>exportService.exportToPDF(exportData,exportHeaders,"BE Bookings",`be_bookings${sfx}.pdf`,false,true,totals,totals)}
          apiEndpoint="transactions/generate-pdf" apiUrl={apiURL}/>

        {/* ── Table ── */}
        <div style={{background:"#fff",borderRadius:16,border:"1px solid #e8eef4",
          boxShadow:"0 2px 16px rgba(0,0,0,0.07)",overflow:"hidden"}}>
          {loading ? (
            <div style={{display:"flex",justifyContent:"center",alignItems:"center",height:320}}>
              <div className="loader"><div className="dot"/><div className="dot"/><div className="dot"/><div className="dot"/><div className="dot"/></div>
            </div>
          ) : error ? (
            <div style={{textAlign:"center",padding:64,color:"#ef4444",fontSize:15}}>{error}</div>
          ) : filtered.length===0 ? (
            <div style={{textAlign:"center",padding:"72px 0"}}>
              <div style={{fontSize:44,marginBottom:12}}>📋</div>
              <div style={{fontSize:18,fontWeight:800,color:"#0f172a",marginBottom:6}}>No bookings found</div>
              <div style={{fontSize:14,color:"#94a3b8",marginBottom:20}}>Adjust the date range or clear your filters.</div>
              <button onClick={()=>{setSF(null);setRF(null);setPF(null);setST("");}}
                style={{padding:"10px 24px",borderRadius:10,border:"1px solid #e2e8f0",background:"#fff",color:"#475569",cursor:"pointer",fontWeight:600,fontSize:13}}>
                Clear filters
              </button>
            </div>
          ) : (
            <Table columns={columns} dataSource={filtered}
              pagination={{
                defaultPageSize:10,
                pageSizeOptions:["10","25","50"],
                showSizeChanger:true,
                showTotal:(t,r)=>`${r[0]}–${r[1]} of ${t} bookings`,
              }}
              rowKey={r=>r.id} scroll={{x:1760}} size="small"
              rowClassName={(_,i)=>i%2===0?"":"row-alt"}/>
          )}
        </div>

        {/* ── Filter Drawer ── */}
        <Drawer open={filterOpen} onClose={()=>setFO(false)} placement="right" width={380}
          title={<span style={{fontWeight:800,fontSize:16}}>Filter bookings</span>}
          zIndex={1100}
          styles={{header:{borderBottom:"1px solid #f0f4f8"},body:{padding:24}}}
          footer={
            <div style={{display:"flex",gap:10,padding:"8px 0"}}>
              <button onClick={()=>{setSF(null);setRF(null);setPF(null);setST("");setFO(false);}}
                style={{flex:1,padding:"11px",borderRadius:10,border:"1px solid #e2e8f0",background:"#fff",color:"#475569",fontWeight:600,cursor:"pointer",fontSize:13}}>
                Reset all
              </button>
              <button onClick={()=>setFO(false)}
                style={{flex:2,padding:"11px",borderRadius:10,border:"none",background:"#1d4ed8",color:"#fff",fontWeight:700,cursor:"pointer",fontSize:13}}>
                Apply
              </button>
            </div>
          }>
          <div style={{display:"flex",flexDirection:"column",gap:20}}>
            {[
              {label:"Booking Status",    opts:statusOptions,  val:statusFilter,  set:setSF},
              {label:"Refund Status",     opts:refundOptions,  val:refundFilter,  set:setRF, hint:"Find pending, failed, or manual refunds."},
              {label:"Payment Method",    opts:payOptions,     val:payFilter,     set:setPF},
            ].map(f=>(
              <div key={f.label}>
                <label style={{fontSize:11,fontWeight:800,color:"#374151",display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.06em"}}>
                  {f.label}
                </label>
                <Select options={f.opts} placeholder={`All ${f.label.toLowerCase()}`}
                  value={f.val} onChange={f.set} isClearable
                  styles={{control:s=>({...s,borderRadius:8,borderColor:"#e2e8f0",minHeight:40}),
                           menu:s=>({...s,zIndex:1200}),menuPortal:s=>({...s,zIndex:1200})}}
                  menuPortalTarget={document.body}/>
                {f.hint&&<div style={{fontSize:11,color:"#94a3b8",marginTop:4}}>{f.hint}</div>}
              </div>
            ))}
            {activeFilters>0&&(
              <div style={{background:"#eff6ff",borderRadius:10,padding:"10px 14px",
                fontSize:12,color:"#1e40af",fontWeight:600}}>
                {activeFilters} filter{activeFilters>1?"s":""} active — {filtered.length} of {bookings.length} bookings shown
              </div>
            )}
          </div>
        </Drawer>

        {/* ── Booking Detail Drawer ── */}
        <BookingDrawer
          open={drawerOpen} booking={selBooking}
          onClose={()=>{setDO(false);setSB(null);}}
          apiURL={apiURL} companyId={companyId}
          onUpdated={fetchBookings}/>
      </div>

      <style>{`
        .row-alt td { background: #fafbfc !important; }
        .ant-table-row:hover td { background: #f0f7ff !important; transition: background 0.12s; }
        .ant-table-thead > tr > th {
          background: #f8fafc !important; font-weight: 800 !important; font-size: 11px !important;
          color: #64748b !important; letter-spacing: 0.06em !important; text-transform: uppercase !important;
          border-bottom: 2px solid #e8eef4 !important; padding: 10px 12px !important;
        }
        .ant-table-tbody > tr > td { border-bottom: 1px solid #f1f5f9 !important; padding: 9px 12px !important; vertical-align: middle !important; }
        .ant-table-container { border-radius: 0 !important; }
        .ant-pagination { padding: 10px 16px !important; margin: 0 !important; border-top: 1px solid #f1f5f9; }
        .ant-drawer-mask { background: rgba(15,23,42,0.5) !important; backdrop-filter: blur(3px); }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; display: inline-block; }
      `}</style>
    </div>
  );
};

export default BeBookingList;